import { DatePicker, Form } from 'antd';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { range } from 'lodash';
import { useState, useEffect } from 'react';

dayjs.extend(isSameOrAfter);

const FormDatePicker = ({
  name,
  width,
  height,
  dateFormat,
  listDisable,
  status,
  isShowTime,
  form,
  allowClear = false,
  defaultFromDate,
  defaultToDate,
  resetTrigger,
  setDateRange,
}) => {
  const [selectedDates, setSelectedDates] = useState([
    defaultFromDate,
    defaultToDate,
  ]);
  const [dateFirstSelect, setDateFirstSelect] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const onOkSelectedDate = (dates) => {
    if (dates) {
      let dateFrom;
      let dateTo;
      if (status === 'SCHEDULED') {
        if (dates[0]) {
          dateFrom = dayjs(dates[0]).isBefore(dayjs()) ? null : dayjs(dates[0]);
          form?.setFields([
            {
              name: name,
              errors: [],
            },
          ]);
        }
      } else {
        dateFrom = dates[0];
      }
      if (dates[1]) {
        dateTo = dates[1];
      }
      form?.setFieldValue(name, [dateFrom, dateTo]);
    }
  };

  const disableRangeTime = (date, _type) => {
    if (!date) {
      return {
        disabledHours: () => range(0, 24),
        disabledMinutes: () => range(0, 60),
      };
    }
    if (dayjs().isSameOrAfter(date, 'hours')) {
      return {
        disabledHours: () => range(0, dayjs().hour()),
        disabledMinutes: () => range(0, dayjs().minute() + 1),
      };
    }
    if (dayjs().isSameOrAfter(date, 'days')) {
      return {
        disabledHours: () => range(0, dayjs().hour()),
      };
    }
  };

  const disabledDate = (current) => {
    const dateFrom = selectedDates?.[0];
    if (dateFirstSelect) {
      return (
        current.isBefore(dayjs(dateFirstSelect).subtract(180, 'day'), 'day') ||
        current.isAfter(dayjs(dateFirstSelect).add(180, 'day'), 'day')
      );
    }
    if (dateFrom) {
      return (
        current.isBefore(dayjs(dateFrom).subtract(180, 'day'), 'day') ||
        current.isAfter(dayjs(dateFrom).add(180, 'day'), 'day')
      );
    }
    return false;
  };

  const handleChange = (dates) => {
    const [date1, date2] = dates;
    if (dayjs(date1).isAfter(dayjs(date2))) {
      setSelectedDates([date2, date1]);
      setDateRange?.({ dateFrom: date2, dateTo: date1 });
    } else {
      setSelectedDates([date1, date2]);
      setDateRange?.({ dateFrom: date1, dateTo: date2 });
    }
  };

  const handleCalendarChange = (dates, dateStrings, info) => {
    if (isCalendarOpen) {
      const diff = Math.abs(dayjs(dates[0]).diff(dayjs(dates[1]), 'day'));
      if (diff > 180) {
        const newDate2 = dayjs(dates[0]).isBefore(dayjs(dates[1]))
          ? dayjs(dates[0]).add(180, 'day')
          : dayjs(dates[0]).subtract(180, 'day');
        const sortedDates = dayjs(dates[0]).isAfter(newDate2)
          ? [newDate2, dates[0]]
          : [dates[0], newDate2];

        setSelectedDates(sortedDates);
        setDateRange?.({ dateFrom: sortedDates[0], dateTo: sortedDates[1] });
        form?.setFieldValue(name, sortedDates);
      }
      if (info.range === 'start' && dates[0]) {
        setDateFirstSelect(dates[0]);
      } else if (info.range === 'end' && dates[1]) {
        setDateFirstSelect(dates[1]);
      }
    }
  };

  useEffect(() => {
    setSelectedDates([defaultFromDate, defaultToDate]);
  }, [resetTrigger]);

  useEffect(() => {
    if (!isCalendarOpen) {
      setDateFirstSelect(null);
    }
  }, [isCalendarOpen]);

  useEffect(() => {
    form?.setFieldValue(name, [defaultFromDate, defaultToDate]);
    setDateRange?.({ dateFrom: defaultFromDate, dateTo: defaultToDate });
  }, []);

  return (
    <Form.Item name={name} style={{ marginBottom: 0 }}>
      <DatePicker.RangePicker
        showTime={
          isShowTime && {
            format: 'HH:mm',
          }
        }
        format={dateFormat}
        style={{ width: width ?? '250px', height: height }}
        placeholder={['日付From', '日付To']}
        disabled={listDisable}
        allowEmpty={true}
        inputReadOnly={true}
        allowClear={allowClear}
        disabledDate={disabledDate}
        disabledTime={disableRangeTime}
        onChange={handleChange}
        onCalendarChange={handleCalendarChange}
        onOpenChange={(open) => {
          setIsCalendarOpen(open);
        }}
        dateRender={(current) => {
          const isSelected =
            (selectedDates[0] && current.isSame(selectedDates[0], 'day')) ||
            (selectedDates[1] && current.isSame(selectedDates[1], 'day'));

          return (
            <div
              className="ant-picker-cell-inner"
              onClick={() => {
                if (isSelected) {
                  setDateFirstSelect(current);
                }
              }}
            >
              {current.date()}
            </div>
          );
        }}
        onOk={onOkSelectedDate}
        value={selectedDates}
      />
    </Form.Item>
  );
};

export default FormDatePicker;
